import { createLogger } from '@svs/nova-core/log';

import type { Draw } from '../../../../schema/draw/entry.js';
import type { DrawForecast, DrawResult } from '../../../../schema/forecast/entry.js';
import {
  type IGetDrawForecastUseCase,
  type IGetDrawResultUseCase,
  type IGetDrawUseCase,
  NotFoundError,
} from '../../../domain/entry.js';
import type { DrawQueryOptions } from '../../../lib/entry.js';

const logger = createLogger('poolbetting.draw-result-controller');

export type IGetDrawResultController = ReturnType<typeof getDrawResultController>;

// When draw is pre live there is no forecast or result, therefore the type is needed so tanstack query won't error out
export type ForecastData =
  | { type: 'FORECAST'; data: DrawForecast }
  | { type: 'RESULT'; data: DrawResult }
  | { type: 'NONE' };

/**
 * Controller to get a specific draw for a product.
 *
 * @param getDrawResultUseCase Use case to get a draw result
 * @param getDrawForecastUseCase use case to get a draw forecast
 * @param getDrawUseCase Use case to get a specific draw
 * @returns Controller executor function to get draw forecast or result
 */
export function getDrawResultController(
  getDrawResultUseCase: IGetDrawResultUseCase,
  getDrawForecastUseCase: IGetDrawForecastUseCase,
  getDrawUseCase: IGetDrawUseCase
) {
  return async function controller(queryInfo: DrawQueryOptions): Promise<ForecastData> {
    let drawStatus: Draw['drawState']['client'] | undefined = undefined;
    try {
      const drawData = await getDrawUseCase.execute(queryInfo);
      drawStatus = drawData.draw.drawState.client;

      if (drawData.draw.drawState.client === 'Finished') {
        const data = await getDrawResultUseCase.execute(queryInfo);

        return { type: 'RESULT', data };
      }

      if (drawData.draw.drawState.client === 'Live') {
        const data = await getDrawForecastUseCase.execute(queryInfo);

        return { type: 'FORECAST', data };
      }
    } catch (error) {
      if (error instanceof NotFoundError) {
        logger.error('Draw forecast not found', { error, drawStatus, queryInfo });
      }

      throw error;
    }

    return { type: 'NONE' };
  };
}
